import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { HiCalendar, HiLocationMarker, HiCurrencyDollar, HiCheck, HiChevronLeft, HiChevronRight } from 'react-icons/hi'
import { getOptimizedImageUrl } from '../lib/utils'

export default function Project({ project }) {
  if (!project || project.is_active === false) return null

  const formattedPrice = project.price 
    ? (/^[\d,\.\s]+$/.test(String(project.price)) && !String(project.price).includes('₪')
        ? `₪${String(project.price).trim()}`
        : project.price)
    : null;

  const details = [
    { icon: <HiCalendar />, label: 'תאריך', value: project.date },
    { icon: <HiLocationMarker />, label: 'מיקום', value: project.location },
    { icon: <HiCurrencyDollar />, label: 'מחיר', value: formattedPrice },
  ].filter(d => d.value)

  const includes = project.details?.includes || []

  return (
    <section id="project" className="py-24 px-6 bg-gradient-to-b from-warm-light to-white w-full flex flex-col items-center overflow-hidden">
      <div className="w-full max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <h2 className="font-rubik text-4xl md:text-5xl font-bold text-text">
            {project.title}
          </h2>
        </motion.div>

        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-12 items-start">
          {/* Column 1: Images */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="w-full"
          >
            <ImageSlider images={[project.image_url, ...(project.details?.images || [])].filter(Boolean)} title={project.title} />
            
            {/* Location Map - Desktop Only: Below Images */}
            {project.location && (
              <div className="mt-12 rounded-3xl overflow-hidden shadow-xl border border-primary-light/20 h-[300px] w-full hidden lg:block" dir="ltr">
                <MapIframe location={project.location} title="Google Maps Location - Desktop" />
              </div>
            )}
          </motion.div>

          {/* Column 2: Content (Details -> Map -> Button) */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="w-full flex flex-col"
          >
            <div className="flex-grow">
              <p className="text-text-light text-lg leading-relaxed mb-8">
                {project.description}
              </p>

              {/* Info cards */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                {details.map((detail, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl p-4 text-center shadow-sm border border-primary-light/10"
                  >
                    <div className="text-primary text-2xl mb-2 flex justify-center">
                      {detail.icon}
                    </div>
                    <div className="text-text-muted text-xs mb-1">{detail.label}</div>
                    <div className="font-semibold text-text text-sm">{detail.value}</div>
                  </div>
                ))}
              </div>

              {/* Includes */}
              {includes.length > 0 && (
                <div className="bg-white/80 rounded-2xl p-6 border border-primary-light/10 mb-8">
                  <h3 className="font-rubik font-semibold text-text mb-4">מה כולל?</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {includes.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-text-light">
                        <HiCheck className="text-secondary flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Location Map - Mobile Only: Below Content */}
            {project.location && (
              <div className="rounded-3xl overflow-hidden shadow-xl border border-primary-light/20 h-[300px] w-full mb-10 lg:hidden" dir="ltr">
                <MapIframe location={project.location} title="Google Maps Location - Mobile" />
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <a
                href="#contact"
                className="w-full sm:w-auto bg-primary hover:bg-primary-dark text-white px-10 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:shadow-lg active:scale-95"
              >
                רוצה להירשם
              </a>
              {project.details?.payment_url && (
                <a
                  href={project.details.payment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto bg-secondary hover:bg-secondary-dark text-white px-10 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:shadow-lg active:scale-95"
                >
                  ביצוע תשלום
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

function MapIframe({ location, title }) {
  const [load, setLoad] = useState(false)
  
  return (
    <div 
      className="w-full h-full bg-warm-light/20 flex items-center justify-center"
      onMouseEnter={() => setLoad(true)}
      onScroll={() => setLoad(true)}
      onClick={() => setLoad(true)}
      onPointerOver={() => setLoad(true)}
    >
      {!load ? (
        <button 
          onClick={() => setLoad(true)}
          className="text-primary-dark font-medium underline underline-offset-4 hover:text-primary transition-colors"
        >
          לחצי כאן להצגת המפה
        </button>
      ) : (
        <iframe
          title={title}
          src={`https://www.google.com/maps?q=${encodeURIComponent(location)}&hl=iw&z=14&output=embed`}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      )}
    </div>
  )
}

function ImageSlider({ images: rawImages, title }) {
  const [index, setIndex] = useState(0)

  if (!rawImages || rawImages.length === 0) return null

  const images = rawImages.map(url => getOptimizedImageUrl(url, 1000, 75))

  const next = () => setIndex((prev) => (prev + 1) % images.length)
  const prev = () => setIndex((prev) => (prev - 1 + images.length) % images.length)

  const handleDragEnd = (event, info) => {
    const threshold = 50
    if (info.offset.x > threshold) {
      prev()
    } else if (info.offset.x < -threshold) {
      next()
    }
  }

  return (
    <div className="relative group touch-pan-y">
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl shadow-2xl bg-gray-100">
        <AnimatePresence initial={false}>
          <motion.img
            key={index}
            src={images[index]}
            alt={`${title} ${index + 1}`}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ type: 'spring', stiffness: 400, damping: 40 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            onDragEnd={handleDragEnd}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover cursor-grab active:cursor-grabbing"
          />
        </AnimatePresence>

        {/* Navigation Arrows - Only on desktop hover */}
        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="תמונה קודמת"
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/40 text-white hover:bg-white/60 transition-all opacity-0 md:group-hover:opacity-100 hidden md:flex"
            >
              <HiChevronLeft size={24} />
            </button>
            <button
              onClick={next}
              aria-label="תמונה הבאה"
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/40 text-white hover:bg-white/60 transition-all opacity-0 md:group-hover:opacity-100 hidden md:flex"
            >
              <HiChevronRight size={24} />
            </button>
          </>
        )}

        {/* Counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-black/30 text-white text-xs font-medium">
          {index + 1} / {images.length}
        </div>
      </div>

      {/* Dots/Mini Previews */}
      {images.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`תמונה ${i + 1}`}
              className={`w-2 h-2 rounded-full transition-all ${
                i === index ? 'bg-primary w-6' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
